import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Loader2, Plus, X, Target, ChevronRight } from "lucide-react";

const CATS = ["career", "finances", "health", "fitness", "business", "education",
  "ai_skills", "productivity", "personal_growth", "travel", "relationships", "parenting"];

export default function Goals() {
  const navigate = useNavigate();
  const [goals, setGoals] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/goals");
    setGoals(data.goals);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!goals) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF7E67]" /></div>;

  return (
    <div className="space-y-8" data-testid="goals-page">
      <div className="flex items-center justify-between">
        <h1 className="font-nunito font-extrabold tracking-tight text-4xl text-[#2D3748] sm:text-5xl">Your Goals</h1>
        <button onClick={() => setOpen(true)} data-testid="new-goal-btn"
          className="flex items-center gap-2 rounded-full bg-sunrise px-5 py-3 text-sm font-bold tracking-wide text-[#2D3748] transition-all hover:scale-105">
          <Plus className="h-4 w-4" /> New Goal
        </button>
      </div>

      {goals.length === 0 && (
        <div className="rounded-3xl border border-[#FFE8E3] bg-white p-12 text-center">
          <Target className="mx-auto h-10 w-10 text-[#FF7E67]" />
          <p className="mt-4 text-[#718096]">No goals yet. Create your first one and let your AI coach build the roadmap.</p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {goals.map((g) => (
          <button key={g.id} onClick={() => navigate(`/app/goals/${g.id}`)} data-testid={`goal-card-${g.id}`}
            className="group rounded-2xl border border-[#FFE8E3] bg-white p-6 text-left transition-all hover:-translate-y-1 hover:border-[#FF7E67]">
            <div className="flex items-center justify-between">
              <span className="rounded-full border border-[#FFE8E3] px-3 py-1 text-xs capitalize text-[#FF7E67]">{(g.category || "").replace("_", " ")}</span>
              <ChevronRight className="h-5 w-5 text-[#A0AEC0] transition-transform group-hover:translate-x-1" />
            </div>
            <h3 className="mt-3 font-nunito text-lg font-bold text-[#2D3748]">{g.title}</h3>
            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[#FFE8E3]">
              <div className="h-full rounded-full bg-sunrise" style={{ width: `${g.progress || 0}%` }} />
            </div>
            <p className="mt-2 text-xs text-[#718096]">{g.progress || 0}% • {g.status}</p>
          </button>
        ))}
      </div>

      {open && <NewGoalModal onClose={() => setOpen(false)} onCreated={(id) => navigate(`/app/goals/${id}`)} />}
    </div>
  );
}

function NewGoalModal({ onClose, onCreated }) {
  const [f, setF] = useState({ title: "", category: "personal_growth", why: "", target_date: "" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (f.title.trim().length < 3) return toast.error("Give your goal a title.");
    setLoading(true);
    try {
      const { data } = await api.post("/goals", f);
      const gid = data.goal.id;
      toast.success("Goal created. Generating roadmap…");
      onCreated(gid);
    } catch (e) { !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail)); setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#2D3748]/50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-3xl border border-[#FFE8E3] bg-white p-7" onClick={(e) => e.stopPropagation()} data-testid="new-goal-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-nunito text-xl font-bold tracking-tight text-[#2D3748]">New Goal</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-[#718096]" /></button>
        </div>
        <div className="mt-5 space-y-3">
          <input placeholder="Goal title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="goal-title-input"
            className="h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
            className="h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 capitalize text-[#2D3748] outline-none focus:border-[#FF7E67]">
            {CATS.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
          <textarea placeholder="Why does this matter?" value={f.why} onChange={(e) => setF({ ...f, why: e.target.value })} rows={2}
            className="w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] p-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
          <input type="date" value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })}
            className="h-11 w-full rounded-xl border border-[#FFE8E3] bg-[#FFF9F5] px-3 text-[#2D3748] outline-none focus:border-[#FF7E67]" />
          <button onClick={submit} disabled={loading} data-testid="create-goal-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-sunrise py-3 font-bold tracking-wide text-[#2D3748] disabled:opacity-70">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create Goal"}
          </button>
        </div>
      </div>
    </div>
  );
}
